/** Address payload aligned with API create-user / partner-master manual profile. */
export type TPartnerMasterManualAddress = {
  street: string;
  number: string;
  complement?: string;
  neighborhood: string;
  city: string;
  state: string;
  country?: string;
  postal_code: string;
};

export type TPartnerMasterManualProfile = {
  first_name: string;
  last_name: string;
  document: string;
  document_type: string;
  phone: string;
  birth_date?: string;
  address: TPartnerMasterManualAddress;
};

export type TCreatePartnerMasterInput = {
  email: string;
  password: string;
  use_representative_data: boolean;
  manual?: TPartnerMasterManualProfile;
};

export enum ETypeOfPartner {
  client = "client",
  business_indicator = "business_indicator",
}
export type TCreateClientInput = {
  name: string;
  cnpj: string;
  corporate_name: string;
  type_partner: ETypeOfPartner;
  email?: string;
  opening_date?: Date;
  signed_terms_id?: string;
  tax_classification?: string;
  business_classification?: string;
  address_id?: string;
};

export type TUpdateClientInput = TClientExtended;

export enum ETaxClassification {
  simples_nacional = "simples_nacional",
  lucro_presumido = "lucro_presumido",
  lucro_real = "lucro_real",
}

export enum EBusinessClassification {
  servicos = "servicos",
  industria = "industria",
  comercial = "comercial",
  agricultura = "agricultura",
  outros = "outros",
}

export enum ClientStatus {
  pending_data = "pending_data",
  pending_review = "pending_review",
  approved = "approved",
  rejected = "rejected",
}

export enum EOrganizationHierarchy {
  primary = "primary",
  secondary = "secondary",
}

export type TClient = {
  id: string;
  organization_hierarchy: EOrganizationHierarchy;
  name: string;
  cnpj: string;
  corporate_name: string;
  email?: string;
  opening_date?: Date;
  signed_terms_id?: string;
  tax_classification?: ETaxClassification;
  business_classification?: EBusinessClassification;
  address_id?: string;
  status?: ClientStatus;
  type_partner?: ETypeOfPartner;
  rejection_reason?: string;
  created_at: Date;
  updated_at: Date;
  deleted_at: Date | null;
};

// Tipo estendido para incluir os campos aninhados do formulário
export type TClientExtended = TClient & {
  address?: {
    postal_code: string;
    street: string;
    number: string;
    complement?: string;
    neighborhood: string;
    city: string;
    state: string;
    country: string;
  };
  company_representative?: {
    first_name: string;
    last_name: string;
    personal_email: string;
    cpf: string;
    mobile_phone: string;
    birth_date?: Date;
  };
  bank_account?: {
    bank_name: string;
    bank_branch: string;
    checking_account: string;
    checking_account_digit: string;
    pix_key?: string | null;
    account_type?: "checking" | "savings";
  };
  document?: {
    company_logo?: File | null;
    cnpj_card?: File | null;
    signed_contract?: File[] | null;
    partners_documents_rg?: File | null;
    partners_documents_cpf?: File | null;
    articles_of_incorporation?: File | null;
    proof_of_address?: File | null;
  };
  signed_terms?: {
    signed: boolean;
    signed_at: string;
    ip_address: string;
  };
};

export type TClientExtendedResponse = TClient & {
  address?: {
    postal_code: string;
    street: string;
    number: string;
    complement?: string;
    neighborhood: string;
    city: string;
    state: string;
    country: string;
  };
  company_representative?: {
    first_name: string;
    last_name: string;
    personal_email: string;
    cpf: string;
    mobile_phone: string;
  };
  bank_account?: {
    bank_name: string;
    bank_branch: string;
    checking_account: string;
    checking_account_digit: string;
    pix_key?: string | null;
  };
  document?: {
    company_logo?: string | null;
    cnpj_card?: string | null;
    signed_contract?: string[] | null;
    partners_documents_rg?: string[] | null;
    partners_documents_cpf?: string[] | null;
    articles_of_incorporation?: string | null;
    proof_of_address?: string | null;
  };
  signed_terms?: {
    signed: boolean;
    signed_at: string;
    ip_address: string;
  };
};

import { TProductWithCapitalRange } from "@/pages/products/products.type";

export type TRejectionData = {
  client_id: string;
  rejection_reason: string;
};

// Interface para a modal de plano
export interface IPlanoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tempPlano: {
    name: string;
    description: string;
    plan_features: string[];
    markup?: number;
    markup_mode?: "percentage" | "real_currency";
    products: Array<{
      id: string;
      capital_value?: number;
      premium_value?: number;
      description?: string;
      insurance_policy_extra_price_id?: string | null;
    }>;
    commission_distribution: {
      agency: number;
      commission: number;
      position: number;
    }[];
    commission_lifetime_other_installments?: number;
  };
  setTempPlano: React.Dispatch<
    React.SetStateAction<{
      name: string;
      description: string;
      plan_features: string[];
      markup?: number;
      markup_mode?: "percentage" | "real_currency";
      products: Array<{
        id: string;
        capital_value?: number;
        premium_value?: number;
        description?: string;
      }>;
      commission_distribution: {
        agency: number;
        commission: number;
        position: number;
      }[];
      commission_lifetime_other_installments?: number;
    }>
  >;
  selectedProducts: TProductWithCapitalRange[];
  editingPlanoIndex?: number;
  onSave: () => void;
  projectType?: "affinity" | "affiliate";
  projectPartners?: Array<{
    id: string;
    name: string;
    commission_percentage: number;
    level_hierarchy: number;
  }>;
}
